import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  Target, 
  Plus, 
  Trash2, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  ExternalLink,
  RefreshCw,
  BarChart3,
  Link2,
  Search,
  Globe
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar } from 'recharts';

interface Competitor {
  id: string;
  name: string;
  domain: string;
  created_at: string;
}

interface CompetitorKeyword {
  id: string;
  competitor_id: string;
  keyword: string;
  our_ranking: number | null;
  competitor_ranking: number | null;
  search_volume: number | null;
  gap: number | null;
  last_updated: string;
}

interface CompetitorBacklink {
  id: string;
  competitor_id: string;
  source_domain: string;
  source_url: string;
  domain_authority: number | null;
  anchor_text: string | null;
  is_opportunity: boolean;
  last_checked: string;
}

export const CompetitorAnalysis = () => {
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [selectedCompetitor, setSelectedCompetitor] = useState<string | null>(null);
  const [competitorKeywords, setCompetitorKeywords] = useState<CompetitorKeyword[]>([]);
  const [competitorBacklinks, setCompetitorBacklinks] = useState<CompetitorBacklink[]>([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newCompetitor, setNewCompetitor] = useState({ name: '', domain: '' });

  // Mock data for demonstration - in production, this would come from the database
  const mockCompetitors: Competitor[] = [
    { id: '1', name: 'TOTO', domain: 'toto.com', created_at: new Date().toISOString() },
    { id: '2', name: 'Kohler', domain: 'kohler.com', created_at: new Date().toISOString() },
    { id: '3', name: 'Duravit', domain: 'duravit.com', created_at: new Date().toISOString() },
  ];

  const mockKeywordData: CompetitorKeyword[] = [
    { id: '1', competitor_id: '1', keyword: 'smart toilet', our_ranking: 15, competitor_ranking: 3, search_volume: 12100, gap: -12, last_updated: new Date().toISOString() },
    { id: '2', competitor_id: '1', keyword: 'bidet toilet seat', our_ranking: 8, competitor_ranking: 5, search_volume: 8100, gap: -3, last_updated: new Date().toISOString() },
    { id: '3', competitor_id: '1', keyword: 'luxury bathroom fixtures', our_ranking: 12, competitor_ranking: 7, search_volume: 5400, gap: -5, last_updated: new Date().toISOString() },
    { id: '4', competitor_id: '1', keyword: 'automatic toilet', our_ranking: 6, competitor_ranking: 4, search_volume: 3600, gap: -2, last_updated: new Date().toISOString() },
    { id: '5', competitor_id: '1', keyword: 'smart bathroom europe', our_ranking: 3, competitor_ranking: 18, search_volume: 2900, gap: 15, last_updated: new Date().toISOString() },
    { id: '6', competitor_id: '1', keyword: 'heated toilet seat', our_ranking: 22, competitor_ranking: 2, search_volume: 9900, gap: -20, last_updated: new Date().toISOString() },
  ];

  const mockBacklinkData: CompetitorBacklink[] = [
    { id: '1', competitor_id: '1', source_domain: 'architecturaldigest.com', source_url: 'https://architecturaldigest.com/smart-bathroom', domain_authority: 92, anchor_text: 'smart bathroom technology', is_opportunity: true, last_checked: new Date().toISOString() },
    { id: '2', competitor_id: '1', source_domain: 'forbes.com', source_url: 'https://forbes.com/luxury-homes', domain_authority: 95, anchor_text: 'luxury fixtures', is_opportunity: true, last_checked: new Date().toISOString() },
    { id: '3', competitor_id: '1', source_domain: 'dezeen.com', source_url: 'https://dezeen.com/bathroom-design', domain_authority: 88, anchor_text: 'innovative toilets', is_opportunity: true, last_checked: new Date().toISOString() },
    { id: '4', competitor_id: '1', source_domain: 'houzz.com', source_url: 'https://houzz.com/smart-toilets', domain_authority: 91, anchor_text: 'best smart toilets', is_opportunity: false, last_checked: new Date().toISOString() },
    { id: '5', competitor_id: '1', source_domain: 'hgtv.com', source_url: 'https://hgtv.com/bathroom-trends', domain_authority: 89, anchor_text: 'bathroom trends 2025', is_opportunity: true, last_checked: new Date().toISOString() },
  ];

  useEffect(() => {
    // Load mock data for now
    setCompetitors(mockCompetitors);
  }, []);

  useEffect(() => {
    if (selectedCompetitor) {
      setCompetitorKeywords(mockKeywordData.filter(k => k.competitor_id === selectedCompetitor));
      setCompetitorBacklinks(mockBacklinkData.filter(b => b.competitor_id === selectedCompetitor));
    }
  }, [selectedCompetitor]);

  const handleAddCompetitor = async () => {
    if (!newCompetitor.name || !newCompetitor.domain) {
      toast.error('Please fill in all fields');
      return;
    }

    const competitor: Competitor = {
      id: Date.now().toString(),
      name: newCompetitor.name,
      domain: newCompetitor.domain.replace(/^https?:\/\//, '').replace(/\/$/, ''),
      created_at: new Date().toISOString()
    };

    setCompetitors([...competitors, competitor]);
    setNewCompetitor({ name: '', domain: '' });
    setIsAddOpen(false);
    toast.success(`Added ${competitor.name} to competitor tracking`);
  };

  const handleRemoveCompetitor = (id: string) => {
    setCompetitors(competitors.filter(c => c.id !== id));
    if (selectedCompetitor === id) {
      setSelectedCompetitor(null);
    }
    toast.success('Competitor removed');
  };

  const handleAnalyzeCompetitor = async (competitorId: string) => {
    setAnalyzing(true);
    setSelectedCompetitor(competitorId);
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    toast.success('Competitor analysis complete');
    setAnalyzing(false);
  };

  const getRankingTrend = (gap: number | null) => {
    if (!gap) return <Minus className="h-4 w-4 text-muted-foreground" />;
    if (gap > 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
    return <TrendingDown className="h-4 w-4 text-red-500" />;
  };

  const getGapBadge = (gap: number | null) => {
    if (!gap) return <Badge variant="secondary">N/A</Badge>;
    if (gap > 0) return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">+{gap}</Badge>;
    return <Badge variant="destructive">{gap}</Badge>;
  };

  // Calculate summary stats
  const keywordsSummary = {
    total: competitorKeywords.length,
    winning: competitorKeywords.filter(k => (k.gap || 0) > 0).length,
    losing: competitorKeywords.filter(k => (k.gap || 0) < 0).length,
    avgGap: competitorKeywords.length > 0 
      ? Math.round(competitorKeywords.reduce((sum, k) => sum + (k.gap || 0), 0) / competitorKeywords.length)
      : 0
  };

  const backlinkOpportunities = competitorBacklinks.filter(b => b.is_opportunity).length;
  const avgDA = competitorBacklinks.length > 0
    ? Math.round(competitorBacklinks.reduce((sum, b) => sum + (b.domain_authority || 0), 0) / competitorBacklinks.length)
    : 0;

  // Chart data
  const keywordChartData = competitorKeywords.map(k => ({
    keyword: k.keyword.length > 15 ? k.keyword.substring(0, 15) + '...' : k.keyword,
    'Our Ranking': k.our_ranking || 100,
    'Competitor': k.competitor_ranking || 100,
    'Search Volume': k.search_volume || 0
  }));

  const backlinkDAChart = competitorBacklinks.map(b => ({
    domain: b.source_domain.replace('.com', '').substring(0, 12),
    DA: b.domain_authority || 0,
    opportunity: b.is_opportunity ? 'Yes' : 'No'
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Target className="h-6 w-6 text-primary" />
            Competitive Analysis
          </h2>
          <p className="text-muted-foreground">Track competitor keyword rankings and backlink profiles</p>
        </div>
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Competitor
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Competitor to Track</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Competitor Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., TOTO, Kohler"
                  value={newCompetitor.name}
                  onChange={(e) => setNewCompetitor({ ...newCompetitor, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="domain">Website Domain</Label>
                <Input
                  id="domain"
                  placeholder="e.g., toto.com"
                  value={newCompetitor.domain}
                  onChange={(e) => setNewCompetitor({ ...newCompetitor, domain: e.target.value })}
                />
              </div>
              <Button onClick={handleAddCompetitor} className="w-full">
                Add Competitor
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Competitors List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Tracked Competitors
          </CardTitle>
        </CardHeader>
        <CardContent>
          {competitors.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No competitors tracked yet. Add your first competitor to start analysis.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {competitors.map((competitor) => (
                <Card 
                  key={competitor.id} 
                  className={`cursor-pointer transition-all hover:border-primary ${selectedCompetitor === competitor.id ? 'border-primary bg-primary/5' : ''}`}
                  onClick={() => setSelectedCompetitor(competitor.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">{competitor.name}</h3>
                        <p className="text-sm text-muted-foreground">{competitor.domain}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAnalyzeCompetitor(competitor.id);
                          }}
                          disabled={analyzing}
                        >
                          <RefreshCw className={`h-4 w-4 ${analyzing && selectedCompetitor === competitor.id ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveCompetitor(competitor.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Analysis Results */}
      {selectedCompetitor && (
        <Tabs defaultValue="keywords" className="space-y-4">
          <TabsList>
            <TabsTrigger value="keywords">
              <Search className="w-4 h-4 mr-2" />
              Keyword Gap Analysis
            </TabsTrigger>
            <TabsTrigger value="backlinks">
              <Link2 className="w-4 h-4 mr-2" />
              Backlink Opportunities
            </TabsTrigger>
          </TabsList>

          <TabsContent value="keywords" className="space-y-6">
            {/* Keyword Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold">{keywordsSummary.total}</div>
                  <div className="text-sm text-muted-foreground">Keywords Tracked</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-green-600">{keywordsSummary.winning}</div>
                  <div className="text-sm text-muted-foreground">Winning</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-red-600">{keywordsSummary.losing}</div>
                  <div className="text-sm text-muted-foreground">Losing</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className={`text-3xl font-bold ${keywordsSummary.avgGap >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {keywordsSummary.avgGap >= 0 ? '+' : ''}{keywordsSummary.avgGap}
                  </div>
                  <div className="text-sm text-muted-foreground">Avg Gap</div>
                </CardContent>
              </Card>
            </div>

            {/* Keyword Comparison Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Ranking Comparison
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={keywordChartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} reversed />
                      <YAxis type="category" dataKey="keyword" width={100} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="Our Ranking" fill="hsl(var(--primary))" />
                      <Bar dataKey="Competitor" fill="#ef4444" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Lower ranking = better position (1 is best)
                </p>
              </CardContent>
            </Card>

            {/* Keyword Table */}
            <Card>
              <CardHeader>
                <CardTitle>Keyword Gap Details</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Keyword</TableHead>
                      <TableHead className="text-center">Our Rank</TableHead>
                      <TableHead className="text-center">Competitor</TableHead>
                      <TableHead className="text-center">Gap</TableHead>
                      <TableHead className="text-center">Search Volume</TableHead>
                      <TableHead className="text-center">Trend</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {competitorKeywords.map((kw) => (
                      <TableRow key={kw.id}>
                        <TableCell className="font-medium">{kw.keyword}</TableCell>
                        <TableCell className="text-center">{kw.our_ranking || '-'}</TableCell>
                        <TableCell className="text-center">{kw.competitor_ranking || '-'}</TableCell>
                        <TableCell className="text-center">{getGapBadge(kw.gap)}</TableCell>
                        <TableCell className="text-center">{kw.search_volume?.toLocaleString() || '-'}</TableCell>
                        <TableCell className="text-center">{getRankingTrend(kw.gap)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="backlinks" className="space-y-6">
            {/* Backlink Summary */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold">{competitorBacklinks.length}</div>
                  <div className="text-sm text-muted-foreground">Backlinks Analyzed</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-green-600">{backlinkOpportunities}</div>
                  <div className="text-sm text-muted-foreground">Opportunities</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-primary">{avgDA}</div>
                  <div className="text-sm text-muted-foreground">Avg Domain Authority</div>
                </CardContent>
              </Card>
            </div>

            {/* DA Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Domain Authority Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={backlinkDAChart}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="domain" />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Bar 
                        dataKey="DA" 
                        fill="hsl(var(--primary))"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Backlink Opportunities Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="h-5 w-5" />
                  Backlink Opportunities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source Domain</TableHead>
                      <TableHead className="text-center">DA</TableHead>
                      <TableHead>Anchor Text</TableHead>
                      <TableHead className="text-center">Opportunity</TableHead>
                      <TableHead className="text-center">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {competitorBacklinks.map((bl) => (
                      <TableRow key={bl.id}>
                        <TableCell className="font-medium">{bl.source_domain}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{bl.domain_authority}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{bl.anchor_text || '-'}</TableCell>
                        <TableCell className="text-center">
                          {bl.is_opportunity ? (
                            <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Yes</Badge>
                          ) : (
                            <Badge variant="secondary">No</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(bl.source_url, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Google Search Console Setup Guide */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Google Search Console Integration Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
              ✓ API Keys Configured
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Your Google Search Console API is configured. To complete setup:
          </p>
          <ol className="text-sm space-y-2 list-decimal list-inside text-muted-foreground">
            <li>Go to <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="text-primary underline">Google Search Console</a></li>
            <li>Add your site property if not already added</li>
            <li>Go to Settings → Users and permissions</li>
            <li>Add your service account email as a user with "Full" permissions</li>
            <li>The system will automatically fetch real ranking data on the next sync</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};
